import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NEVER_QUEUED, OFFLINE_REPLAYED, sendBillingMutation } from '../../lib/billing-mutations';
import { invalidateAfterSale } from '../../lib/invalidate-after-sale';
import type {
  CashRegisterSessionWire,
  CashMovementWire,
  POSTransactionWire,
  POSItemResultWire,
  POSTransactionMessageResultWire,
  POSPaymentResultWire,
  POSCheckoutResultWire,
  POSSaleIssueIncidentWire,
} from '../../wire/pos';
import type { McfTaxpayerWarningWire, ProvisionalOfferWire, TillPaymentMethodCode } from '@efacture/fiscal-core';
import type { BillingTransport } from '../../billing-host/types';
import { useBillingApi } from '../../billing-host/api';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

// Session types
export type CashMovementType = 'OPENING' | 'CLOSING' | 'DEPOSIT' | 'WITHDRAWAL' | 'SALE' | 'REFUND';

export interface CashMovement {
  id: string;
  sessionId: string;
  type: CashMovementType;
  amount: number;
  reason?: string | null;
  reference?: string | null;
  createdAt: string;
}

export interface POSTransaction {
  id: string;
  tenantId: string;
  operatorId: string;
  sessionId: string;
  status: TransactionStatus;
  invoiceType: InvoiceType;
  priceMode: PriceMode;
  /**
   * Currency the sale was opened in and is settled in; null on a sale
   * opened before sales stored one, settled in the certification currency
   * of the day.
   */
  currency?: string | null;
  clientType: ClientType;
  clientId?: string | null;
  clientName?: string | null;
  clientIfu?: string | null;
  totalHT: number;
  totalTVA: number;
  totalTTC: number;
  totalDiscount: number;
  amountPaid: number;
  changeGiven: number;
  invoiceId?: string | null;
  lines: POSTransactionLine[];
  payments: POSPayment[];
  suspendedAt?: string | null;
  suspendedReason?: string | null;
  completedAt?: string | null;
  createdAt: string;
}

export interface CashRegisterSession {
  id: string;
  tenantId: string;
  operatorId: string;
  pointOfSale: string;
  openingBalance: number;
  closingBalance?: number | undefined;
  expectedClosing?: number | undefined;
  discrepancy?: number | undefined;
  openedAt: string;
  closedAt?: string | null;
  currentBalance?: number;
  movements?: CashMovement[] | undefined;
  transactions?: POSTransaction[] | undefined;
}

// Transaction types
export type TransactionStatus = 'DRAFT' | 'PENDING' | 'PAYING' | 'COMPLETED' | 'CANCELLED' | 'SUSPENDED';
export type PaymentMethod = TillPaymentMethodCode;
/** Code of the client-type table (DGI codes plus the tenant's rows, §2.35). */
export type ClientType = string;
export type InvoiceType = 'FV' | 'FT' | 'EV' | 'ET';
export type PriceMode = 'HT' | 'TTC';

export interface POSTransactionLine {
  id: string;
  transactionId: string;
  lineNumber: number;
  articleId: string;
  articleCode: string;
  articleName: string;
  taxGroup: string;
  psvbGroup?: string | null;
  quantity: number;
  unitPrice: number;
  discount: number;
  totalHT: number;
  totalTVA: number;
  /** TSB of the line, inside its TTC. */
  totalTSB: number;
  /** Specific tax of the line, inside its TTC. */
  totalSpecificTax: number;
  specificTaxLabel?: string | null | undefined;
  totalTTC: number;
}

export interface POSPayment {
  id: string;
  transactionId: string;
  method: PaymentMethod;
  amount: number;
  /** POINTS: the loyalty points the payment spends, fixed when it was added. */
  points?: number | null;
  reference?: string | null;
  provider?: string | null;
  createdAt: string;
}

// Input types
export interface OpenSessionInput {
  pointOfSale: string;
  openingBalance: number;
  /** The depot this shift draws from; the server resolves the default when absent. */
  stockLocationId?: string;
}

export interface CloseSessionInput {
  closingBalance: number;
  /** Recorded as the reason of the session's CLOSING cash movement. */
  notes?: string | undefined;
}

export interface AddCashMovementInput {
  type: CashMovementType;
  amount: number;
  reason?: string | undefined;
  reference?: string | undefined;
}

export interface CreateTransactionInput {
  invoiceType?: InvoiceType;
  priceMode?: PriceMode;
  clientType?: ClientType;
}

export interface AddItemInput {
  articleId: string;
  articleCode: string;
  articleName: string;
  articleType: string;
  taxGroup: string;
  psvbGroup?: string | undefined;
  quantity: number;
  /**
   * Price override, in the cart's price mode; the server refuses it (403)
   * without `pos:price-override`. Without it the server takes the
   * article's catalogue price converted into that mode.
   */
  unitPrice?: number | undefined;
  priceOverrideReason?: string | undefined;
  discount?: number | undefined;
}

export interface UpdateItemQuantityInput {
  quantity: number;
  /** Price override, in the cart's price mode; requires `pos:price-override`. */
  unitPrice?: number | undefined;
  priceOverrideReason?: string | undefined;
}

export interface SetClientInput {
  clientType: ClientType;
  clientId?: string | undefined;
  clientName?: string | undefined;
  clientIfu?: string | undefined;
}

export interface AddPaymentInput {
  method: PaymentMethod;
  amount: number;
  reference?: string | undefined;
  provider?: string | undefined;
  /** AVOIR: the signed credit note the payment applies. */
  creditNoteId?: string | undefined;
}

/** A signed credit note the cart can pay with, and its balance not yet applied. */
export interface PosApplicableCreditNote {
  id: string;
  number: string;
  signedAt: string | null;
  /** The site that issued it: two sites can hold one number. */
  issuingSite?: string | null;
  available: number;
}

export interface SuspendTransactionInput {
  reason?: string;
}

export interface ClientLookupResult {
  id: string;
  type: ClientType;
  name?: string;
  firstName?: string;
  ifu?: string;
  phone?: string;
  email?: string;
  address?: string;
}

// ─────────────────────────────────────────────────────────────
// Response envelopes
// ─────────────────────────────────────────────────────────────
//
// The POS routes do NOT all answer with a bare transaction, and they cannot:
// `POST /payments` carries `remaining` and `changeGiven`, `POST /complete`
// carries `invoiceNumber`, `invoiceNim`, `loyaltyPointsEarned` and
// `stockDeducted` — none of which is derivable from the transaction row. So
// the envelope stays server-side and is unwrapped HERE, which leaves exactly
// one rule for this file: every function returns what its name says, and the
// two genuinely multi-valued calls return a NAMED result type whose extra
// fields the caller reads deliberately.
//
// ★ Why the runtime guard below exists.
//   `api.post<T>` is a cast across an untyped HTTP boundary — the type
//   parameter is a promise the compiler cannot check. `addItem` was declared
//   `Promise<POSTransaction>` while handing back `{ line, transaction }`;
//   TypeScript believed the annotation, `transaction.lines` was `undefined`,
//   the cart rendered « Panier vide » on a sale the database had already
//   accepted, and « Payer » / « Vider » / « Suspendre » all key off
//   `lines.length` — one click left the register open and un-closeable.
//   An annotation cannot prevent that recurring; a check at the boundary can.
//   Every function below therefore goes through `assertPOSTransaction`, so a
//   future divergence fails LOUDLY, in French, at the call that caused it,
//   instead of silently emptying a cashier's cart.

/** Reply of `POST /api/pos/transactions/:id/payments`. */
export interface POSPaymentResult {
  payment: POSPayment;
  transaction: POSTransaction;
  /** Amount still due after this payment, in FCFA. */
  remaining: number;
  /** Change owed to the customer, in FCFA. */
  changeGiven: number;
}

/** Reply of `POST /api/pos/transactions/:id/complete`. */
export interface POSCheckoutResult {
  transaction: POSTransaction;
  invoiceId: string;
  /** DGI invoice number — this, not `invoiceId`, is what a customer is shown. */
  invoiceNumber: string;
  invoiceNim: string;
  /** Device that signed the invoice (`FISCAL_MODALITIES`); null when none is recorded. */
  fiscalModality: string | null;
  /** `ISSUANCE_REGIMES` code the invoice was issued in; null when the reply names none. */
  issuanceRegime: string | null;
  changeGiven: number;
  loyaltyPointsEarned: number;
  stockDeducted: boolean;
  /** The certifying MCF's taxpayer information (2Bh) differs from the printed seller; null when it does not. */
  mcfTaxpayerWarning: McfTaxpayerWarningWire | null;
}

/**
 * A POS transaction is only usable by the cart if it carries its identity and
 * its lines: every control on the page (« Payer », « Vider », « Suspendre »,
 * the totals) is derived from `lines`.
 */
function isPOSTransaction(value: unknown): value is POSTransactionWire {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { id?: unknown; lines?: unknown };
  return typeof candidate.id === 'string' && Array.isArray(candidate.lines);
}

/**
 * Prisma `Decimal` fields (quantity, unitPrice, totalTTC, ...) arrive as
 * JSON strings; this converts them to the numbers the POS types declare,
 * so `sum + line.quantity` adds instead of concatenating. Applied to every
 * transaction this module returns. Its parameter is the actual wire shape
 * (`POSTransactionWire`, @efacture/core): every Decimal field is still a
 * string at this point, unlike the post-normalization `POSTransaction` this
 * function returns.
 */
function normalizeTransactionNumerics(transaction: POSTransactionWire): POSTransaction {
  return {
    ...transaction,
    // status/invoiceType/priceMode/method are untyped DB columns; zod
    // constrains them to these unions on write — the value itself is
    // untouched, only its declared type narrows.
    status: transaction.status as TransactionStatus,
    invoiceType: transaction.invoiceType as InvoiceType,
    priceMode: transaction.priceMode as PriceMode,
    totalHT: num(transaction.totalHT),
    totalTVA: num(transaction.totalTVA),
    totalTTC: num(transaction.totalTTC),
    totalDiscount: num(transaction.totalDiscount),
    amountPaid: num(transaction.amountPaid),
    changeGiven: num(transaction.changeGiven),
    lines: transaction.lines.map((line) => ({
      ...line,
      quantity: num(line.quantity),
      unitPrice: num(line.unitPrice),
      discount: num(line.discount),
      totalHT: num(line.totalHT),
      totalTVA: num(line.totalTVA),
      totalTSB: num(line.totalTSB),
      totalSpecificTax: num(line.totalSpecificTax),
      totalTTC: num(line.totalTTC),
    })),
    payments: transaction.payments.map((payment) => ({
      ...payment,
      method: payment.method as PaymentMethod,
      amount: num(payment.amount),
    })),
  };
}

function assertPOSTransaction(value: unknown, operation: string): POSTransaction {
  if (!isPOSTransaction(value)) {
    throw new Error(
      `Réponse inattendue du serveur (${operation}) : la vente n'a pas pu être lue. Rechargez la page avant de continuer.`
    );
  }
  return normalizeTransactionNumerics(value);
}

/**
 * Converts a session's Decimal-as-string fields to numbers; a null
 * closing/expected/discrepancy (open session) becomes `undefined`. Carried
 * transactions go through `normalizeTransactionNumerics`, except one with
 * no `lines` array, left as-is for the POS resume guard to skip.
 */
function normalizeSession(session: CashRegisterSessionWire): CashRegisterSession {
  return {
    ...session,
    openingBalance: num(session.openingBalance),
    closingBalance: optionalNum(session.closingBalance),
    expectedClosing: optionalNum(session.expectedClosing),
    discrepancy: optionalNum(session.discrepancy),
    movements: session.movements?.map(normalizeMovement),
    // A transaction with no `lines` array skips normalization (the resume
    // guard treats it as unusable and never reads its numeric fields) — its
    // Decimal fields stay strings despite the `POSTransaction` type.
    transactions: session.transactions?.map((transaction) =>
      Array.isArray(transaction.lines) ? normalizeTransactionNumerics(transaction) : (transaction as unknown as POSTransaction)
    ),
  };
}

function normalizeMovement(movement: CashMovementWire): CashMovement {
  return {
    ...movement,
    // Untyped DB column; zod constrains it to CashMovementType on write.
    type: movement.type as CashMovementType,
    amount: num(movement.amount),
  };
}

/** Unwrap a `{ …, transaction }` envelope, checking what it actually contains. */
function unwrapTransaction(envelope: unknown, operation: string): POSTransaction {
  const inner = (envelope as { transaction?: unknown } | null | undefined)?.transaction;
  return assertPOSTransaction(inner, operation);
}

// ─────────────────────────────────────────────────────────────
// Query Keys
// ─────────────────────────────────────────────────────────────

export const posKeys = {
  all: ['pos'] as const,
  sessions: () => [...posKeys.all, 'sessions'] as const,
  activeSession: () => [...posKeys.sessions(), 'active'] as const,
  session: (id: string) => [...posKeys.sessions(), id] as const,
  transactions: () => [...posKeys.all, 'transactions'] as const,
  transaction: (id: string) => [...posKeys.transactions(), id] as const,
  saleProvisionalOffer: (id: string) => [...posKeys.transaction(id), 'provisional-offer'] as const,
  suspendedTransactions: () => [...posKeys.transactions(), 'suspended'] as const,
  clients: () => [...posKeys.all, 'clients'] as const,
  clientByIfu: (ifu: string) => [...posKeys.clients(), 'ifu', ifu] as const,
  applicableCreditNotes: (clientId: string | null, transactionId: string | null, number: string | null = null) =>
    [...posKeys.all, 'applicableCreditNotes', clientId ?? '', transactionId ?? '', number ?? ''] as const,
};

/**
 * Hook: signed credit notes that can pay the cart `transactionId`: its client's,
 * those naming that client by typed fields, and on a sale with no directory
 * client the bearer ones the user may read, or the one numbered `number` the
 * customer presents — GET /api/pos/credit-notes/applicable
 */
export function useApplicablePosCreditNotes(
  clientId: string | null,
  options?: { enabled?: boolean; number?: string | null; transactionId?: string | null }
) {
  const api = useBillingApi();
  const number = options?.number ?? null;
  const transactionId = options?.transactionId ?? null;
  return useQuery({
    queryKey: posKeys.applicableCreditNotes(clientId, transactionId, number),
    queryFn: async () => {
      const params = {
        ...(clientId ? { clientId } : {}),
        ...(transactionId ? { transactionId } : {}),
        ...(number ? { number } : {}),
      };
      const response = await api.get<{ creditNotes: PosApplicableCreditNote[] }>(
        '/pos/credit-notes/applicable',
        Object.keys(params).length > 0 ? params : undefined
      );
      return response.creditNotes;
    },
    enabled: options?.enabled ?? true,
  });
}

// ─────────────────────────────────────────────────────────────
// API Functions - Sessions
// ─────────────────────────────────────────────────────────────

async function fetchActiveSession(api: BillingTransport): Promise<CashRegisterSession | null> {
  const session = await api.get<CashRegisterSessionWire | null>('/pos/sessions/active');
  return session ? normalizeSession(session) : null;
}

async function openSession(api: BillingTransport, input: OpenSessionInput): Promise<CashRegisterSession> {
  const session = await sendBillingMutation<CashRegisterSessionWire>(api, 'POST', '/pos/sessions', input);
  return normalizeSession(session);
}

async function closeSession(api: BillingTransport, id: string, input: CloseSessionInput): Promise<CashRegisterSession> {
  const session = await sendBillingMutation<CashRegisterSessionWire>(api, 'POST', `/pos/sessions/${id}/close`, input);
  return normalizeSession(session);
}

async function addCashMovement(api: BillingTransport, sessionId: string, input: AddCashMovementInput): Promise<CashMovement> {
  const movement = await sendBillingMutation<CashMovementWire>(api, 'POST', `/pos/sessions/${sessionId}/movements`, input);
  return normalizeMovement(movement);
}

// ─────────────────────────────────────────────────────────────
// API Functions - Transactions
// ─────────────────────────────────────────────────────────────

// Bare-transaction routes: the API answers with the transaction itself.
// Still checked — an annotation is not a contract (see the note above).

async function createTransaction(api: BillingTransport, input: CreateTransactionInput): Promise<POSTransaction> {
  const payload = await sendBillingMutation<unknown>(api, 'POST', '/pos/transactions', input);
  return assertPOSTransaction(payload, 'création de la vente');
}

async function fetchTransaction(api: BillingTransport, id: string): Promise<POSTransaction> {
  const payload = await api.get<unknown>(`/pos/transactions/${id}`);
  return assertPOSTransaction(payload, 'lecture de la vente');
}

async function setClient(api: BillingTransport, transactionId: string, input: SetClientInput): Promise<POSTransaction> {
  const payload = await sendBillingMutation<unknown>(api, 'PUT', `/pos/transactions/${transactionId}/client`, input);
  return assertPOSTransaction(payload, 'sélection du client');
}

// Enveloped routes: `{ line, transaction }` or `{ message, transaction }`.
// The extra field is of no use to any caller, so it is dropped here rather
// than leaked into the page.

async function addItem(api: BillingTransport, transactionId: string, input: AddItemInput): Promise<POSTransaction> {
  const payload = await sendBillingMutation<POSItemResultWire>(
    api, 'POST',
    `/pos/transactions/${transactionId}/items`,
    input
  );
  return unwrapTransaction(payload, "ajout d'un article");
}

async function updateItemQuantity(
  api: BillingTransport, transactionId: string,
  lineId: string,
  input: UpdateItemQuantityInput
): Promise<POSTransaction> {
  const payload = await sendBillingMutation<POSItemResultWire>(
    api, 'PATCH',
    `/pos/transactions/${transactionId}/items/${lineId}`,
    input
  );
  return unwrapTransaction(payload, 'modification de la quantité');
}

async function removeItem(api: BillingTransport, transactionId: string, lineId: string): Promise<POSTransaction> {
  const payload = await sendBillingMutation<POSTransactionMessageResultWire>(
    api, 'DELETE',
    `/pos/transactions/${transactionId}/items/${lineId}`
  );
  return unwrapTransaction(payload, "retrait d'un article");
}

async function removePayment(api: BillingTransport, transactionId: string, paymentId: string): Promise<POSTransaction> {
  const payload = await sendBillingMutation<POSTransactionMessageResultWire>(
    api, 'DELETE',
    `/pos/transactions/${transactionId}/payments/${paymentId}`
  );
  return unwrapTransaction(payload, "retrait d'un paiement");
}

async function cancelTransaction(api: BillingTransport, transactionId: string): Promise<POSTransaction> {
  const payload = await sendBillingMutation<POSTransactionMessageResultWire>(
    api, 'POST',
    `/pos/transactions/${transactionId}/cancel`
  );
  return unwrapTransaction(payload, 'annulation de la vente');
}

async function suspendTransaction(
  api: BillingTransport, transactionId: string,
  input?: SuspendTransactionInput
): Promise<POSTransaction> {
  const payload = await sendBillingMutation<POSTransactionMessageResultWire>(
    api, 'POST',
    `/pos/transactions/${transactionId}/suspend`,
    input
  );
  return unwrapTransaction(payload, 'suspension de la vente');
}

async function resumeTransaction(api: BillingTransport, transactionId: string): Promise<POSTransaction> {
  const payload = await sendBillingMutation<POSTransactionMessageResultWire>(
    api, 'POST',
    `/pos/transactions/${transactionId}/resume`
  );
  return unwrapTransaction(payload, 'reprise de la vente');
}

// Genuinely multi-valued routes: the extra fields are NOT derivable from the
// transaction row, so they keep a named result type instead of being thrown away.

// Two rules, and they differ on purpose. A missing `transaction` THROWS: the
// cart cannot continue without one, and failing loudly is the whole point.
// A missing scalar is NORMALIZED instead — by the time `complete` answers, the
// sale is signed and the stock is deducted; turning a malformed `changeGiven`
// into an error would report a successful checkout as a failure. So the fields
// below are read, not asserted.

function num(value: unknown): number {
  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  return typeof parsed === 'number' && !Number.isNaN(parsed) ? parsed : 0;
}

function optionalNum(value: unknown): number | undefined {
  return value === null || value === undefined ? undefined : num(value);
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function nullableStr(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

async function addPayment(
  api: BillingTransport, transactionId: string,
  input: AddPaymentInput
): Promise<POSPaymentResult> {
  const payload = (await sendBillingMutation<POSPaymentResultWire>(
    api, 'POST',
    `/pos/transactions/${transactionId}/payments`,
    input
  )) as unknown as Record<string, unknown> | null;
  return {
    payment: (payload?.['payment'] ?? null) as POSPayment,
    transaction: unwrapTransaction(payload, "ajout d'un paiement"),
    remaining: num(payload?.['remaining']),
    changeGiven: num(payload?.['changeGiven']),
  };
}

/**
 * Completes a sale; with `provisional`, issues it as a provisional document
 * (arrêté n°2025-0049, art. 6 g)), or the server refuses it.
 */
async function completeTransaction(
  api: BillingTransport,
  transactionId: string,
  options: { provisional?: boolean } = {}
): Promise<POSCheckoutResult> {
  const payload = (await (options.provisional === true
    ? sendBillingMutation<POSCheckoutResultWire>(api, 'POST', `/pos/transactions/${transactionId}/complete`, {
        provisional: true,
      })
    : sendBillingMutation<POSCheckoutResultWire>(
        api,
        'POST',
        `/pos/transactions/${transactionId}/complete`
      ))) as unknown as Record<string, unknown> | null;
  return {
    transaction: unwrapTransaction(payload, 'finalisation de la vente'),
    invoiceId: str(payload?.['invoiceId']),
    invoiceNumber: str(payload?.['invoiceNumber']),
    invoiceNim: str(payload?.['invoiceNim']),
    fiscalModality: nullableStr(payload?.['fiscalModality']),
    issuanceRegime: nullableStr(payload?.['issuanceRegime']),
    changeGiven: num(payload?.['changeGiven']),
    loyaltyPointsEarned: num(payload?.['loyaltyPointsEarned']),
    stockDeducted: payload?.['stockDeducted'] === true,
    mcfTaxpayerWarning: taxpayerWarning(payload?.['mcfTaxpayerWarning']),
  };
}

/** The reply's MCF taxpayer mismatch when it carries a message to show; null otherwise. */
function taxpayerWarning(value: unknown): McfTaxpayerWarningWire | null {
  if (typeof value !== 'object' || value === null) return null;
  const warning = value as Partial<McfTaxpayerWarningWire>;
  return typeof warning.message === 'string' && warning.message !== '' ? (value as McfTaxpayerWarningWire) : null;
}

/** What the till offers for a sale not issued yet: the provisional path (`POSSaleIssueIncidentWire`). */
export interface POSSaleProvisionalOffer {
  provisionalOffer: ProvisionalOfferWire | null;
}

function provisionalOfferOf(value: unknown): ProvisionalOfferWire | null {
  const offer = value as Record<string, unknown> | null | undefined;
  if (typeof offer !== 'object' || offer === null || typeof offer['accepted'] !== 'boolean') return null;
  const outageId = offer['outageId'];
  if (outageId !== null && typeof outageId !== 'string') return null;
  const refusal = offer['refusal'] as Record<string, unknown> | null | undefined;
  return {
    outageId,
    accepted: offer['accepted'],
    refusal:
      refusal && typeof refusal['code'] === 'string' && typeof refusal['message'] === 'string'
        ? { code: refusal['code'], message: refusal['message'] }
        : null,
  };
}

/**
 * GET /api/pos/transactions/:id/dgi-incident — the provisional path the
 * server offers for the sale; a reply it cannot read offers nothing.
 */
async function fetchSaleProvisionalOffer(api: BillingTransport, transactionId: string): Promise<POSSaleProvisionalOffer> {
  const payload = (await api.get<POSSaleIssueIncidentWire>(
    `/pos/transactions/${transactionId}/dgi-incident`
  )) as unknown as Record<string, unknown> | null;
  return { provisionalOffer: provisionalOfferOf(payload?.['provisionalOffer']) };
}

export interface SuspendedTransactionsPagination {
  page: number;
  limit: number;
  /** Every suspended sale in the operator's scope, across all pages. */
  total: number;
  totalPages: number;
  hasMore: boolean;
}

interface SuspendedTransactionsResponse {
  transactions: POSTransactionWire[];
  pagination: SuspendedTransactionsPagination;
}

export interface SuspendedTransactionsPage {
  transactions: POSTransaction[];
  pagination: SuspendedTransactionsPagination;
}

/** Rows per page of the suspended list; the route accepts at most 50. */
export const SUSPENDED_PAGE_SIZE = 10;

/**
 * GET /api/pos/transactions/suspended — one page; a non-blank `search`
 * filters on client name, client IFU and sale id, and `pagination.total`
 * counts the filtered set.
 */
async function fetchSuspendedTransactions(
  api: BillingTransport, page = 1,
  limit = SUSPENDED_PAGE_SIZE,
  search = ''
): Promise<SuspendedTransactionsPage> {
  const term = search.trim();
  const response = await api.get<SuspendedTransactionsResponse>('/pos/transactions/suspended', {
    page,
    limit,
    ...(term ? { search: term } : {}),
  });
  return {
    transactions: response.transactions.map(normalizeTransactionNumerics),
    pagination: response.pagination,
  };
}

// ─────────────────────────────────────────────────────────────
// API Functions - CRM
// ─────────────────────────────────────────────────────────────

async function lookupClientByIfu(api: BillingTransport, ifu: string): Promise<ClientLookupResult | null> {
  return api.get<ClientLookupResult | null>(`/pos/clients/lookup-ifu/${encodeURIComponent(ifu)}`);
}

// ─────────────────────────────────────────────────────────────
// Queries - Sessions
// ─────────────────────────────────────────────────────────────

/**
 * Hook to fetch the active cash register session
 * GET /api/pos/sessions/active
 */
export function useActiveSession(options?: { enabled?: boolean }) {
  const api = useBillingApi();
  return useQuery({
    queryKey: posKeys.activeSession(),
    queryFn: () => fetchActiveSession(api),
    staleTime: 1000 * 60 * 1, // 1 minute
    enabled: options?.enabled ?? true,
  });
}

// ─────────────────────────────────────────────────────────────
// Queries - Transactions
// ─────────────────────────────────────────────────────────────

/**
 * Hook: the provisional path the till offers for a sale not issued yet —
 * GET /api/pos/transactions/:id/dgi-incident
 */
export function useSaleProvisionalOffer(transactionId: string | null, options?: { enabled?: boolean }) {
  const api = useBillingApi();
  return useQuery({
    queryKey: posKeys.saleProvisionalOffer(transactionId ?? ''),
    queryFn: () => fetchSaleProvisionalOffer(api, transactionId!),
    staleTime: 1000 * 30,
    enabled: (options?.enabled ?? true) && !!transactionId,
  });
}

/**
 * Hook to fetch one page of the suspended transactions
 * GET /api/pos/transactions/suspended?page=&limit=&search=
 * `pagination.total` counts every matching suspended sale, not only this page.
 */
export function useSuspendedTransactions(options?: {
  enabled?: boolean;
  page?: number;
  limit?: number;
  search?: string;
}) {
  const api = useBillingApi();
  const page = options?.page ?? 1;
  const limit = options?.limit ?? SUSPENDED_PAGE_SIZE;
  const search = options?.search?.trim() ?? '';
  return useQuery({
    queryKey: [...posKeys.suspendedTransactions(), { page, limit, search }] as const,
    queryFn: () => fetchSuspendedTransactions(api, page, limit, search),
    staleTime: 1000 * 60 * 1, // 1 minute
    enabled: options?.enabled ?? true,
  });
}

// ─────────────────────────────────────────────────────────────
// Queries - CRM
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
// Mutations - Sessions
// ─────────────────────────────────────────────────────────────

/**
 * Hook to open a new cash register session
 * POST /api/pos/sessions
 */
export function useOpenSession() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: (input: OpenSessionInput) => openSession(api, input),
    onSuccess: (session) => {
      // Update active session in cache
      queryClient.setQueryData(posKeys.activeSession(), session);
      // Invalidate sessions list
      queryClient.invalidateQueries({ queryKey: posKeys.sessions() });
    },
  });
}

/**
 * Hook to close the current cash register session
 * POST /api/pos/sessions/:id/close
 */
export function useCloseSession() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: ({ id, input }: { id: string; input: CloseSessionInput }) => closeSession(api, id, input),
    onSuccess: () => {
      // Clear active session from cache
      queryClient.setQueryData(posKeys.activeSession(), null);
      // Invalidate sessions list
      queryClient.invalidateQueries({ queryKey: posKeys.sessions() });
    },
  });
}

/**
 * Hook to add a cash movement to a session
 * POST /api/pos/sessions/:id/movements
 */
export function useAddCashMovement() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: ({ sessionId, input }: { sessionId: string; input: AddCashMovementInput }) =>
      addCashMovement(api, sessionId, input),
    onSuccess: (_, { sessionId }) => {
      // Invalidate the specific session
      queryClient.invalidateQueries({ queryKey: posKeys.session(sessionId) });
      // Invalidate active session to refresh balance
      queryClient.invalidateQueries({ queryKey: posKeys.activeSession() });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Mutations - Transactions
// ─────────────────────────────────────────────────────────────

/**
 * Hook to create a new POS transaction
 * POST /api/pos/transactions
 */
export function useCreateTransaction() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...OFFLINE_REPLAYED,
    mutationFn: (input: CreateTransactionInput) => createTransaction(api, input),
    onSuccess: (transaction) => {
      // Add transaction to cache
      queryClient.setQueryData(posKeys.transaction(transaction.id), transaction);
    },
  });
}

/**
 * Hook to add an item to a transaction
 * POST /api/pos/transactions/:id/items
 */
export function useAddItem() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: ({ transactionId, input }: { transactionId: string; input: AddItemInput }) =>
      addItem(api, transactionId, input),
    onSuccess: (updatedTransaction) => {
      // Update transaction in cache
      queryClient.setQueryData(posKeys.transaction(updatedTransaction.id), updatedTransaction);
    },
  });
}

/**
 * Hook to update item quantity in a transaction
 * PATCH /api/pos/transactions/:id/items/:lid
 */
export function useUpdateItemQuantity() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: ({
      transactionId,
      lineId,
      input,
    }: {
      transactionId: string;
      lineId: string;
      input: UpdateItemQuantityInput;
    }) => updateItemQuantity(api, transactionId, lineId, input),
    onSuccess: (updatedTransaction) => {
      // Update transaction in cache
      queryClient.setQueryData(posKeys.transaction(updatedTransaction.id), updatedTransaction);
    },
  });
}

/**
 * Hook to remove an item from a transaction
 * DELETE /api/pos/transactions/:id/items/:lid
 */
export function useRemoveItem() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: ({ transactionId, lineId }: { transactionId: string; lineId: string }) =>
      removeItem(api, transactionId, lineId),
    onSuccess: (updatedTransaction) => {
      // Update transaction in cache
      queryClient.setQueryData(posKeys.transaction(updatedTransaction.id), updatedTransaction);
    },
  });
}

/**
 * Hook to set or update the client on a transaction
 * PUT /api/pos/transactions/:id/client
 */
export function useSetClient() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: ({ transactionId, input }: { transactionId: string; input: SetClientInput }) =>
      setClient(api, transactionId, input),
    onSuccess: (updatedTransaction) => {
      // Update transaction in cache
      queryClient.setQueryData(posKeys.transaction(updatedTransaction.id), updatedTransaction);
    },
  });
}

/**
 * Hook to add a payment to a transaction
 * POST /api/pos/transactions/:id/payments
 */
export function useAddPayment() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: ({ transactionId, input }: { transactionId: string; input: AddPaymentInput }) =>
      addPayment(api, transactionId, input),
    onSuccess: (result) => {
      // Update transaction in cache
      queryClient.setQueryData(posKeys.transaction(result.transaction.id), result.transaction);
    },
  });
}

/**
 * Hook to remove a payment from a transaction
 * DELETE /api/pos/transactions/:id/payments/:pid
 */
export function useRemovePayment() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: ({ transactionId, paymentId }: { transactionId: string; paymentId: string }) =>
      removePayment(api, transactionId, paymentId),
    onSuccess: (updatedTransaction) => {
      // Update transaction in cache
      queryClient.setQueryData(posKeys.transaction(updatedTransaction.id), updatedTransaction);
    },
  });
}

/**
 * Hook to complete a transaction (finalize and sign)
 * POST /api/pos/transactions/:id/complete
 */
export function useCompleteTransaction() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: ({ transactionId, provisional }: { transactionId: string; provisional?: boolean }) =>
      completeTransaction(api, transactionId, { provisional: provisional === true }),
    // Whether the sale may now be issued as a provisional document is the
    // server's answer to a fresh read, whatever the error.
    onError: (_error, { transactionId }) => {
      queryClient.invalidateQueries({ queryKey: posKeys.saleProvisionalOffer(transactionId) });
    },
    onSuccess: (result) => {
      // Update transaction in cache
      queryClient.setQueryData(posKeys.transaction(result.transaction.id), result.transaction);
      // Invalidate active session to update balance
      queryClient.invalidateQueries({ queryKey: posKeys.activeSession() });
      // Invalidate suspended transactions list
      queryClient.invalidateQueries({ queryKey: posKeys.suspendedTransactions() });
      // The sale changed stock, invoices, dashboard KPIs and stock overview.
      invalidateAfterSale(queryClient);
    },
  });
}

/**
 * Hook to cancel a transaction
 * POST /api/pos/transactions/:id/cancel
 */
export function useCancelTransaction() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: (transactionId: string) => cancelTransaction(api, transactionId),
    onSuccess: (cancelledTransaction) => {
      // Update transaction in cache
      queryClient.setQueryData(posKeys.transaction(cancelledTransaction.id), cancelledTransaction);
      // Invalidate suspended transactions list
      queryClient.invalidateQueries({ queryKey: posKeys.suspendedTransactions() });
    },
  });
}

/**
 * Hook to suspend a transaction
 * POST /api/pos/transactions/:id/suspend
 */
export function useSuspendTransaction() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: ({ transactionId, input }: { transactionId: string; input?: SuspendTransactionInput }) =>
      suspendTransaction(api, transactionId, input),
    onSuccess: (suspendedTransaction) => {
      // Update transaction in cache
      queryClient.setQueryData(posKeys.transaction(suspendedTransaction.id), suspendedTransaction);
      // Invalidate suspended transactions list
      queryClient.invalidateQueries({ queryKey: posKeys.suspendedTransactions() });
    },
  });
}

/**
 * Hook to resume a suspended transaction
 * POST /api/pos/transactions/:id/resume
 */
export function useResumeTransaction() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: (transactionId: string) => resumeTransaction(api, transactionId),
    onSuccess: (resumedTransaction) => {
      // Update transaction in cache
      queryClient.setQueryData(posKeys.transaction(resumedTransaction.id), resumedTransaction);
      // Invalidate suspended transactions list
      queryClient.invalidateQueries({ queryKey: posKeys.suspendedTransactions() });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Utility Exports
// ─────────────────────────────────────────────────────────────

export {
  // Session API functions
  fetchActiveSession,
  openSession,
  closeSession,
  addCashMovement,
  // Transaction API functions
  createTransaction,
  fetchTransaction,
  addItem,
  updateItemQuantity,
  removeItem,
  setClient,
  addPayment,
  removePayment,
  completeTransaction,
  fetchSaleProvisionalOffer,
  cancelTransaction,
  suspendTransaction,
  resumeTransaction,
  fetchSuspendedTransactions,
  // CRM API functions
  lookupClientByIfu,
};
